import React, { useState, useEffect } from "react";
import DriveFolderUploadRoundedIcon from "@mui/icons-material/DriveFolderUploadRounded";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useTranslation } from "react-i18next";

interface FormikFormSelectProps {
  fieldName: string;
  labelName: string;
  formik: any;
  type?: string;
  disabled?: boolean;
  accept?: string;
  maxSize?: number;
  initialFile?: File;
}

const FormikImageUploadForm = (props: FormikFormSelectProps) => {
  const { t } = useTranslation();
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [removeFile, setRemoveFile] = useState<boolean>(false);
  
  const validateFile = (file: File): string | null => {
    if (props.maxSize && file.size > props.maxSize * 1024 * 1024) {
      return t("errors.file-too-large", { maxSize: props.maxSize });
    }

    const acceptedTypes = props.accept?.split(",") || [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
    ];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    if (
      !acceptedTypes.some((type) =>
        type.startsWith(".")
          ? fileExtension === type
          : file.type.match(type.replace("*", ".*"))
      )
    ) {
      return t("errors.invalid-file-type");
    }

    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const validationError = validateFile(file);

      if (validationError) {
        setCertificateFile(null);
        props.formik.setFieldValue(props.fieldName, null);
        props.formik.setFieldError(props.fieldName, validationError);
        props.formik.setFieldTouched(props.fieldName, true);
      } else {
        setCertificateFile(file);
        props.formik.setFieldValue(props.fieldName, file);
        props.formik.setFieldTouched(props.fieldName, true);
        props.formik.setFieldError(props.fieldName, undefined);
      }
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const validationError = validateFile(file);

      if (validationError) {
        setCertificateFile(null);
        props.formik.setFieldValue(props.fieldName, null);
        props.formik.setFieldError(props.fieldName, validationError);
        props.formik.setFieldTouched(props.fieldName, true);
      } else {
        setCertificateFile(file);
        props.formik.setFieldValue(props.fieldName, file);
        props.formik.setFieldTouched(props.fieldName, true);
        props.formik.setFieldError(props.fieldName, false);
      }
    }
  };

  const handleRemoveFile = () => {
    setCertificateFile(null);
    setRemoveFile(false);
    props.formik.setFieldValue(props.fieldName, null);
    props.formik.setFieldTouched(props.fieldName, true);
    props.formik.setFieldError(props.fieldName, undefined);
  };
  
  useEffect(() => {
    const formikValue = props.formik.values[props.fieldName];
    setCertificateFile(formikValue instanceof File ? formikValue : null);
  }, [props.formik.values, props.fieldName]);
  

  useEffect(() => {
    if (props.initialFile && !removeFile)
        setCertificateFile(props.initialFile);
  }, [props.initialFile, removeFile]);

  return (
    <>
      <div className="mt-6">
        <h1 className="text-xl font-semibold mb-2">
          {t(`labels.attach-${props.fieldName}`)}
        </h1>
        <div
          className={`border-2 border-dashed rounded-lg p-6 bg-gray-50 flex flex-col items-center justify-center cursor-pointer transition-colors duration-200 ${
            isDragOver
              ? "border-blue-400 bg-blue-50"
              : "border-blue-600"
          }`}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onClick={() =>
            !props.disabled &&
            document.getElementById("certificate-upload")?.click()
          }
          style={{ pointerEvents: props.disabled ? "none" : "auto" }}
        >
          <div className="mb-2">
            <DriveFolderUploadRoundedIcon
              sx={{
                fontSize: 48,
                color: "#016AD4",
              }}
            />
          </div>
          <div
            className= "text-gray-700"
            
          >
            {props.disabled
              ? t("labels.upload-disabled")
              : t("labels.drag-your-file-to-start-uploading")}
          </div>
          {!props.disabled && (
            <>
              <div className="my-2 text-gray-400 text-sm">OR</div>
              <label
                htmlFor="certificate-upload"
                className="inline-block px-4 py-1 border border-blue-600 text-blue-600 rounded-md font-medium cursor-pointer hover:bg-blue-600 hover:text-white transition-colors duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                {t("labels.browse-files")}
              </label>
            </>
          )}
          <input
            id="certificate-upload"
            name="certificate"
            type="file"
            accept={props.accept || ".pdf,.jpg,.jpeg,.png"}
            onChange={handleFileChange}
            className="hidden"
            disabled={props.disabled}
          />
        </div>

        {certificateFile && (
          <div className="mt-2 text-xl text-gray-600 border-2 rounded-lg p-2 w-fit flex items-center">
            <InsertDriveFileIcon sx={{ fontSize: 32, color: "#016AD4" }} />
            <span className="ml-2">{certificateFile.name}</span>
            {!props.disabled && (
              <HighlightOffIcon
                sx={{
                  fontSize: 16,
                  color: "#FF0000",
                  cursor: "pointer",
                  marginLeft: "7px",
                }}
                onClick={handleRemoveFile}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default FormikImageUploadForm;
